'use client';

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  useBodyScrollLock,
  usePrefersReducedMotion,
} from '@paeffgen/react/crm-documents/util';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  // Wechsel triggert scrollTop=0 im inneren Scroll-Container — fuer Sheets,
  // die ihren Inhalt austauschen ohne selbst neu gemountet zu werden.
  contentKey?: string;
  // Höhere Stapel-Ebene, wenn das Sheet über einem anderen BottomSheet liegt
  // (z. B. Positions-Aktionen über dem Beleg-Sheet). Default: Basis-Ebene.
  elevated?: boolean;
}

// Swipe-to-close: ab dieser Strecke nach unten oder dieser Geschwindigkeit
// (Pixel pro Millisekunde) wird das Sheet geschlossen.
const SWIPE_CLOSE_DISTANCE_PX = 120;
const SWIPE_CLOSE_VELOCITY = 0.5;
// iOS-Sheet-Curve — fühlt sich am nativsten an.
const SHEET_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SHEET_DURATION_MS = 280;

export function BottomSheet({
  open,
  onClose,
  ariaLabel,
  header,
  footer,
  children,
  className,
  contentKey,
  elevated,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ y: number; time: number } | null>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const animMs = prefersReducedMotion ? 0 : SHEET_DURATION_MS;

  useEffect(() => {
    if (open) {
      setMounted(true);
      // 2x rAF: erst rendert React mit translateY(100%), dann togglen wir
      // auf translateY(0) — so läuft die CSS-Transition statt instant zu
      // springen.
      let frame2 = 0;
      const frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(frame1);
        cancelAnimationFrame(frame2);
      };
    }
    setEntered(false);
    const t = window.setTimeout(() => setMounted(false), animMs);
    return () => window.clearTimeout(t);
  }, [open, animMs]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useBodyScrollLock(mounted);

  useEffect(() => {
    if (!open || !sheetRef.current) return;
    const focusable = sheetRef.current.querySelector<HTMLElement>(
      'input, button, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [contentKey]);

  useEffect(() => {
    if (!open) {
      setDragY(0);
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [open]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartRef.current = { y: e.clientY, time: Date.now() };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return;
    const delta = e.clientY - dragStartRef.current.y;
    // Nur nach unten — leichter Widerstand nach oben (rubber-band-Effekt).
    setDragY(delta > 0 ? delta : delta / 4);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return;
    const delta = e.clientY - dragStartRef.current.y;
    const duration = Math.max(1, Date.now() - dragStartRef.current.time);
    const velocity = delta / duration;
    dragStartRef.current = null;
    setIsDragging(false);
    if (delta > SWIPE_CLOSE_DISTANCE_PX || velocity > SWIPE_CLOSE_VELOCITY) {
      onClose();
    } else {
      setDragY(0);
    }
  };

  if (!mounted) return null;

  // Gestapelt über einem Basis-Sheet (z. B. Positions-Aktionen über dem
  // Beleg-Sheet) — sonst Basis-Ebene.
  const backdropZ = elevated ? 'z-[80]' : 'z-[60]';
  const sheetZ = elevated ? 'z-[81]' : 'z-[70]';

  const sheetTransform =
    dragY > 0
      ? `translateY(${dragY}px)`
      : entered
        ? 'translateY(0)'
        : 'translateY(100%)';

  return (
    <>
      <div
        className={clsx('md:hidden fixed inset-0 bg-black/40', backdropZ)}
        onClick={onClose}
        aria-hidden="true"
        style={{
          // margin:0 neutralisiert einen evtl. vom umgebenden Tailwind-`space-y-*`
          // Container vererbten Sibling-Margin — der würde das fixed-Backdrop/Sheet
          // sonst vom unteren Viewport-Rand wegschieben (Lücke unter dem Sheet).
          margin: 0,
          opacity: entered ? 1 : 0,
          transition: `opacity ${animMs}ms ease-out`,
          pointerEvents: entered ? 'auto' : 'none',
        }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={clsx(
          'md:hidden fixed inset-x-0 bottom-0 flex flex-col bg-white rounded-t-2xl shadow-2xl dark:shadow-none dark:ring-1 dark:ring-white/10 max-h-[90dvh]',
          sheetZ,
          className,
        )}
        style={{
          // Siehe Backdrop: verhindert eine Lücke unter dem Sheet, falls ein
          // umgebender `space-y-*`-Container einen Sibling-Margin vererbt.
          margin: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: sheetTransform,
          transition: isDragging
            ? 'none'
            : `transform ${animMs}ms ${SHEET_EASING}`,
          touchAction: isDragging ? 'none' : undefined,
          willChange: 'transform',
        }}
      >
        {/* Drag-Zone: Balken + Header sind gemeinsam nach unten ziehbar — der
            Nutzer muss nicht den schmalen Balken treffen. Setzt voraus, dass
            der Header KEINE interaktiven Elemente (Buttons/Links) enthält:
            das sofortige setPointerCapture beim pointerdown würde deren Klicks
            sonst abfangen. Aktuell sind alle Header rein textuell. */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
        >
          <div
            role="button"
            tabIndex={-1}
            aria-label="Zum Schließen nach unten ziehen"
            className="flex justify-center pt-2 pb-2"
          >
            <span className="w-10 h-1 rounded-full bg-zinc-300" />
          </div>

          {header && <div className="border-b border-zinc-100">{header}</div>}
        </div>

        <button
          onClick={onClose}
          aria-label="Schließen"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
        >
          <X size={20} />
        </button>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          {children}
        </div>

        {footer && (
          <div className="shrink-0 border-t border-zinc-200 bg-white px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
